import {Fragment,useEffect,useRef,useState} from 'react';
import { Pagination, usePagedResource } from './paged-list.js';
import {FeedSettings} from './feed-settings.js';
import {FocalPicker} from './focal-picker.js';
import {EditionReview} from './edition-review.js';
import {selectTelegramBulk} from './evening-bulk-selection.js';
type Item = Record<string, any>;
type Requester = (route: string, method?: string, body?: unknown) => Promise<any>;
const storageKey = 'diggingbeagle-evening-draft-v1';
const savedDraft = () => { try { return JSON.parse(localStorage.getItem(storageKey)??'{}'); } catch { return {}; } };
const stamp = (value: unknown) => value && Number.isFinite(Date.parse(String(value)))
  ? <time dateTime={String(value)} title={new Date(String(value)).toISOString()}>{new Date(String(value)).toLocaleString(undefined,{timeZoneName:'short'})}</time> : <>Unknown</>;
const originLabel: Record<string,string> = {radar:'News feed',telegram:'Telegram forward',operator:'Operator idea',dot:'Dot import'};
const phaseLabel: Record<string,string> = {
  draft:'Draft',
  preparing:'Preparing',
  prepared:'Prepared',
  returned:'Returned',
  reviewed:'Reviewed',
  applied:'Applied',
  publication_approved:'Publication approved',
  candidate:'Candidate',
  active:'Active',
  cancelled:'Cancelled'
};

export function EveningDesk({ request,refreshVersion=0 }: { request: Requester;refreshVersion?:number }) {
  const initial = useRef(savedDraft());
  const [query,setQuery]=useState(initial.current.query??''),[origin,setOrigin]=useState(initial.current.origin??'all'),[page,setPage]=useState(initial.current.page??1);
  const [savedQuery,setSavedQuery]=useState(''),[savedPhase,setSavedPhase]=useState('all'),[savedOutput,setSavedOutput]=useState('all'),[savedSort,setSavedSort]=useState('updated'),[savedPage,setSavedPage]=useState(1),[savedPageSize,setSavedPageSize]=useState(20);
  const [expandedEditionId,setExpandedEditionId]=useState<string|null>(initial.current.edition_id??null);
  const [showHidden,setShowHidden]=useState(false),[showReleased,setShowReleased]=useState(false),[intakeRefresh,setIntakeRefresh]=useState(0);
  const [inboxMarked,setInboxMarked]=useState<Set<string>>(new Set());
  const [selected,setSelected]=useState<Set<string>>(new Set(initial.current.selected??[]));
  const [focal,setFocal]=useState<string[]>(initial.current.focal??[]);
  const [focus,setFocus]=useState(initial.current.focus??''),[depth,setDepth]=useState(initial.current.depth??'deep');
  const [busy,setBusy]=useState(false),[restoring,setRestoring]=useState(Boolean(initial.current.edition_id)),[error,setError]=useState(''),[notice,setNotice]=useState('');
  const [edition,setEdition]=useState<Item|null>(null),[job,setJob]=useState<Item|null>(null),[session,setSession]=useState<Item|null>(null),[desk,setDesk]=useState<Item|null>(null);
  const key=useRef(initial.current.key??crypto.randomUUID());
  const url=`/api/inbox/evening?page=${page}&page_size=20${origin!=='all'?`&origin=${encodeURIComponent(origin)}`:''}&q=${encodeURIComponent(query)}&include_hidden=${showHidden?'true':'false'}&include_released=${showReleased?'true':'false'}`;
  const intake=usePagedResource<Item>(url,250,refreshVersion+intakeRefresh);
  const savedUrl=`/api/evening?page=${savedPage}&page_size=${savedPageSize}&q=${encodeURIComponent(savedQuery)}&phase=${encodeURIComponent(savedPhase)}&output=${encodeURIComponent(savedOutput)}&sort=${encodeURIComponent(savedSort)}`;
  const savedRefresh=refreshVersion+(edition?.updated_at&&Number.isFinite(Date.parse(String(edition.updated_at)))?Date.parse(String(edition.updated_at)):0);
  const saved=usePagedResource<Item>(savedUrl,250,savedRefresh);
  useEffect(()=>{if(!refreshVersion)return;void request('/evening?page=1&page_size=1').then(setDesk).catch(cause=>setError(String(cause)));if(edition)void request(`/editions/${edition.edition_id}`).then(setEdition).catch(cause=>setError(String(cause)));},[refreshVersion]);
  const resume=async(id:string,preserveDraft=false)=>{
    setRestoring(true);setError('');
    try {
      const value=await request(`/editions/${id}`),draft=preserveDraft&&initial.current.edition_id===id&&initial.current.selection_version===Number(value.selection_version)?initial.current:null;setEdition(value);setFocus(draft?.focus??value.focus);setDepth(draft?.depth??value.depth);setFocal(draft?.focal??value.focal_resource_ids);setSelected(new Set(draft?.selected??value.selected_inputs.map((item:Item)=>item.raw_item_id)));setSession(null);setJob(null);
      if(value.last_job_id)setJob(await request(`/jobs/${value.last_job_id}`));
    }catch(cause){setError(cause instanceof Error?cause.message:String(cause));}finally{setRestoring(false);}
  };
  useEffect(()=>{
    void request('/evening?page=1&page_size=1').then(setDesk).catch(cause=>setError(String(cause)));
    if(initial.current.edition_id)void resume(initial.current.edition_id,true);
  },[]);
  useEffect(()=>{
    if(restoring)return;
    localStorage.setItem(storageKey,JSON.stringify({key:key.current,edition_id:edition?.edition_id,selection_version:edition?Number(edition.selection_version):null,job_id:job?.job_id,selected:[...selected],focus,focal,depth,query,origin,page}));
  },[edition,job,selected,focus,focal,depth,query,origin,page,restoring]);
  useEffect(()=>{
    if(!job||!['queued','running'].includes(job.state))return;
    let stopped=false,inFlight=false;
    const poll=()=>{
      if(stopped||document.hidden||inFlight)return;
      inFlight=true;
      void request(`/jobs/${job.job_id}`).then(async next=>{
        const value=next.state==='succeeded'&&edition?await request(`/editions/${edition.edition_id}`):null;
        if(stopped)return;
        if(value)setEdition(value);
        setJob(next);
      }).catch(cause=>{if(!stopped)setError(String(cause));}).finally(()=>{inFlight=false;});
    };
    const timer=setTimeout(poll,1000);
    document.addEventListener('visibilitychange',poll);
    return()=>{stopped=true;clearTimeout(timer);document.removeEventListener('visibilitychange',poll);};
  },[job,edition?.edition_id]);
  const toggle=(id:string)=>setSelected(current=>{const next=new Set(current);next.has(id)?next.delete(id):next.size<30&&next.add(id);return next;});
  const selection={focus:focus.trim(),depth,raw_item_ids:[...selected],focal_resource_ids:focal};
  const changed=edition&&(edition.focus!==selection.focus||edition.depth!==depth||JSON.stringify(edition.focal_resource_ids)!==JSON.stringify(focal)||JSON.stringify(edition.selected_inputs.map((item:Item)=>item.raw_item_id))!==JSON.stringify(selection.raw_item_ids));
  const save=async()=>{
    if(edition&&!changed)return edition;
    const value=edition?await request(`/editions/${edition.edition_id}/selection`,'PUT',{...selection,selection_version:Number(edition.selection_version)})
      :await request('/editions','POST',{...selection,idempotency_key:key.current,title:selection.focus.slice(0,240)});
    const full=await request(`/editions/${value.edition_id}`);setEdition(full);setSession(null);return full;
  };
  const run=async(action:()=>Promise<void>)=>{setBusy(true);setError('');setNotice('');try{await action();}catch(cause){setError(cause instanceof Error?cause.message:String(cause));}finally{setBusy(false);}};
  const toggleInboxMarked=(id:string)=>setInboxMarked(current=>{
    const next=new Set(current);
    next.has(id)?next.delete(id):next.add(id);
    return next;
  });

  const setInboxVisibility=(ids:string[],hidden:boolean)=>run(async()=>{
    if(!ids.length)return;
    const result=await request('/inbox/evening/visibility','POST',{
      raw_item_ids:ids,
      hidden
    });
    setInboxMarked(new Set());
    setIntakeRefresh(value=>value+1);
    setNotice(`${result.changed} intake item${result.changed===1?'':'s'} ${hidden?'hidden':'restored'}.`);
  });

  const checkAllTelegram=()=>run(async()=>{
    // The existing research selection is distinct from Hide/Restore markings.
    // Respect the saved 30-item limit, existing selections and current search.
    const parameters=new URLSearchParams({
      page:'1', page_size:'100', origin:'telegram', q:query,
      include_hidden:'false', include_released:String(showReleased)
    });
    const result=await request(`/inbox/evening?${parameters}`);
    const merged=selectTelegramBulk([...selected],result.items??[],30);
    setSelected(new Set(merged.ids));
    setNotice(`${merged.added} Telegram input${merged.added===1?'':'s'} selected. ${
      merged.truncated||Number(result.total??0)>100
        ? 'Selection is limited to 30; narrow the search or deselect items to include others.'
        : 'Review and deselect anything irrelevant before preparing the packet.'}`);
  });

  const hideAllMatching=()=>run(async()=>{
    const result=await request('/inbox/evening/visibility','POST',{
      all_matching:true,
      hidden:true,
      q:query,
      origin,
      include_released:showReleased
    });
    setInboxMarked(new Set());
    setPage(1);
    setIntakeRefresh(value=>value+1);
    setNotice(`${result.changed} matching intake item${result.changed===1?'':'s'} hidden.`);
  });
  const prepare=()=>run(async()=>{
    const value=await save();
    const receipt=await request(`/editions/${value.edition_id}/prepare`,'POST',{request_key:`prepare:${value.edition_id}:${value.selection_version}${job?.state==='cancelled'?':'+crypto.randomUUID():''}`});setJob(receipt);
    setNotice('Selection saved. Packet preparation is durable; you can leave and return. Intake is not marked processed.');
  });
  const newEdition=()=>{setExpandedEditionId('new');key.current=crypto.randomUUID();setEdition(null);setJob(null);setSession(null);setFocus('');setFocal([]);setSelected(new Set());setNotice('New evening selection. Existing editions remain saved.');};
  const selectionLocked=Boolean(edition&&!["draft","prepared"].includes(edition.phase));
  const pending=busy||restoring||Boolean(job&&['queued','running'].includes(job.state));
  const packetReady=Boolean(edition?.packet&&edition.packet.task_status==='active'&&!changed);
  const rows=intake.data?.items??[];
  const eveningWorkspace=<div className="saved-evening-inline-workspace">
<div className="desk-grid">
      <section className="panel intake-picker"><h2>Choose the useful material</h2><div className="desk-toolbar"><label>Search all available intake<input maxLength={240} value={query} onChange={event=>{setQuery(event.target.value);setPage(1);}} placeholder="Headline or forwarded text"/></label><label>Origin<select value={origin} onChange={event=>{setOrigin(event.target.value);setPage(1);}}><option value="all">All origins</option>{Object.entries(originLabel).map(([value,label])=><option key={value} value={value}>{label}</option>)}</select></label></div>
        <div className="intake-bulk-actions">
          <label className="checkbox-label">
            <input type="checkbox" checked={showReleased}
              onChange={event=>{setShowReleased(event.target.checked);setPage(1);setInboxMarked(new Set());}}/>
            Show released
          </label>
          <label className="checkbox-label">
            <input type="checkbox" checked={showHidden}
              onChange={event=>{setShowHidden(event.target.checked);setPage(1);setInboxMarked(new Set());}}/>
            Show hidden
          </label>
          <span>{inboxMarked.size} marked</span>
          <button type="button" className="subtle"
            disabled={pending||selectionLocked||selected.size>=30}
            onClick={()=>void checkAllTelegram()}>
            Check all Telegram (max 30)
          </button>
          <button type="button" className="subtle"
            disabled={pending||!inboxMarked.size}
            onClick={()=>void setInboxVisibility([...inboxMarked],true)}>
            Hide marked
          </button>
          <button type="button" className="subtle"
            disabled={pending||!inboxMarked.size}
            onClick={()=>void setInboxVisibility([...inboxMarked],false)}>
            Restore marked
          </button>
          <button type="button" className="subtle"
            disabled={pending||!rows.length}
            onClick={()=>void setInboxVisibility(rows.map((item:Item)=>item.raw_item_id),true)}>
            Hide page
          </button>
          <button type="button" className="subtle"
            disabled={pending||!intake.data?.total}
            onClick={()=>void hideAllMatching()}>
            Hide all matching
          </button>
        </div>
        <p className="muted">{selected.size} of 30 selected across pages · {intake.data?.total??'—'} matching inputs</p>
        {intake.loading&&<p role="status">Loading intake summaries…</p>}
        {!intake.loading&&!intake.error&&intake.data&&!rows.length&&<p>No matching inputs. You can still prepare an idea-only packet.</p>}
        <div className="desk-rows">{rows.map(item=><article className="desk-row" key={item.raw_item_id}>
          <div className="desk-row-checks">
<input id={`input-${item.raw_item_id}`} type="checkbox" checked={selected.has(item.raw_item_id)} onChange={()=>toggle(item.raw_item_id)} disabled={pending||selectionLocked||(!selected.has(item.raw_item_id)&&selected.size>=30)}/>
          <label className="hide-mark" title="Mark for hide or restore">
            <input type="checkbox"
              checked={inboxMarked.has(item.raw_item_id)}
              onChange={()=>toggleInboxMarked(item.raw_item_id)}
              disabled={pending}/>
            <span>hide</span>
          </label>
        </div>
          <div className="desk-item"><label htmlFor={`input-${item.raw_item_id}`}><strong>{String(item.body_preview||item.external_key).split('\n')[0]}</strong></label><span className="badge">{originLabel[item.source]??item.source} · {item.status}</span>{item.released&&<span className="badge">released</span>}{item.operator_hidden_at&&<span className="badge">hidden</span>}
            <small>{(item.feed_provenance?.[0]?.publisher||item.canonical_url)&&<>{item.feed_provenance?.[0]?.publisher||new URL(item.canonical_url).hostname} · </>}Received {stamp(item.received_at)}</small><small>Source published {stamp(item.original_published_at)}{item.associated_resource_id?` · already linked to coverage`:''}{item.feed_provenance?.length>1?` · found in ${item.feed_provenance.length} feeds (deduplicated)`:''}</small>
            <details><summary>Text and provenance</summary><p>{item.body_preview}</p>{item.original_url&&<a href={item.original_url} target="_blank" rel="noreferrer">Read original source</a>}{item.associated_resource_id&&<p>Exact linked resource: <code>{item.associated_resource_id}</code></p>}<p>{item.telegram_forward?.origin?.type??item.telegram_forward?.type??'Original intake preserved in the packet'} · attachment {item.attachment?'present':'none'}</p></details>
          </div></article>)}</div><Pagination page={intake.data} onPage={setPage} disabled={intake.loading}/>
      </section>
      <section className="panel edition-builder"><h2>{edition?'Saved evening focus':'Your question or idea'}</h2><p>{edition?`${edition.workstream_title} · ${edition.phase}`:'Selected material and your idea belong to the same Workstream.'}</p>
        <label>Research focus<textarea required maxLength={4000} disabled={pending||selectionLocked} value={focus} onChange={event=>setFocus(event.target.value)} placeholder="What should tonight’s research establish, compare or update?"/></label><label>Depth<select value={depth} disabled={pending||selectionLocked} onChange={event=>setDepth(event.target.value)}>{['brief','standard','deep','synthesis'].map(value=><option key={value}>{value}</option>)}</select></label>
        <p>{selected.size?`${selected.size} inputs selected.`:'Idea-only packet.'} {changed?'Selection changes are not yet pinned.':edition?'Selection is pinned.':'Selection is private; no packet prepared.'}</p>
        <FocalPicker selected={focal} onChange={setFocal} disabled={pending||selectionLocked}/>
        <div className="actions"><button className="subtle" disabled={pending||selectionLocked||!focus.trim()} onClick={()=>void run(async()=>{await save();setNotice('Selection saved to this Workstream.');})}>Save selection</button></div>
        {job&&<section className={job.state==='failed'?'alert':'job-receipt'} aria-live="polite"><h3>Packet preparation · {job.state}</h3><p>{job.cancel_requested&&job.state==='running'?'Cancellation requested':job.stage.replaceAll('_',' ')}{job.total!=null?` · ${job.processed??0}/${job.total}`:''}</p><p>Last progress {stamp(job.last_progress_at)}</p>{job.state==='failed'&&<><p>{job.error}</p>{job.retryable&&<button disabled={busy} onClick={()=>void run(async()=>setJob(await request(`/jobs/${job.job_id}/retry`,'POST',{request_key:crypto.randomUUID()})))}>Retry saved preparation</button>}</>}{['queued','running'].includes(job.state)&&<button className="subtle" disabled={busy||job.cancel_requested} onClick={()=>void run(async()=>setJob(await request(`/jobs/${job.job_id}/cancel`,'POST',{})))}>Cancel preparation</button>}</section>}
        {edition?.packet?.task_status==='cancelled'&&edition?.packet&&<div className="notice"><p>This packet’s research was cancelled. Its immutable history remains, but access is revoked.</p><button disabled={pending} onClick={()=>void run(async()=>{await request(`/editions/${edition.edition_id}/selection`,'PUT',{...selection,selection_version:Number(edition.selection_version)});setEdition(await request(`/editions/${edition.edition_id}`));setJob(null);setNotice('New selection version saved. Prepare a new read-only packet.');})}>Save a fresh selection version</button></div>}{packetReady&&edition&&<section className="packet-ready"><h3>Immutable packet ready</h3><p>Prepared {stamp(edition.packet.created_at)}</p><div className="actions"><a className="button-link" href={`/api/editions/${edition.edition_id}/packets/${edition.packet.packet_id}/download`}>Download portable packet</a><button disabled={busy} onClick={()=>void run(async()=>setSession(await request(`/research/tasks/${edition.packet.task_id}/sessions`,'POST',{ttl_seconds:21600})))}>Issue read-only research link</button></div><details><summary>Packet identity and hash</summary><code>{edition.packet.manifest_sha256}</code></details></section>}
        {session&&<div className="notice"><h3>Read-only research link</h3><p>Copy now. This bearer URL is not saved in the edition.</p><input readOnly value={session.url??''} aria-label="Read-only research session URL"/><a href={`${session.url}edition/portable`} target="_blank" rel="noreferrer">Open pinned research packet</a></div>}
        {edition&&<a href={`#workstream=${edition.workstream_id}`}>Continue in this Workstream</a>}
      </section>
    </div>
    {edition?.last_packet_id&&<EditionReview key={edition.edition_id} refreshVersion={refreshVersion} edition={edition} request={request} onState={async()=>{setEdition(await request(`/editions/${edition.edition_id}`));}}/>}
  </div>;
  return <section className="evening-desk" aria-busy={restoring}>
    <header className="desk-summary"><div><h2>Tonight’s research</h2><p>Choose useful news and forwards, add your question, then prepare one read-only packet. Nothing publishes automatically.</p></div><dl><div><dt>Available intake</dt><dd>{desk?.counts?.available??'—'}</dd></div><div><dt>Last received</dt><dd>{stamp(desk?.counts?.freshest_at)}</dd></div></dl></header>
    <section className="panel saved-research">
      <header className="saved-research-head">
        <div>
          <h2>Saved research</h2>
          <p>{saved.data?`${saved.data.total} saved ${saved.data.total===1?'evening':'evenings'}`:'Search and resume earlier research.'}</p>
        </div>
        <button type="button" className="subtle" disabled={pending} onClick={newEdition}>Start a new evening</button>
      </header>

      <div className="saved-research-toolbar">
        <label>Search
          <input
            maxLength={240}
            value={savedQuery}
            onChange={event=>{setSavedQuery(event.target.value);setSavedPage(1);}}
            placeholder="Title or research question"
          />
        </label>

        <label>Status
          <select value={savedPhase} onChange={event=>{setSavedPhase(event.target.value);setSavedPage(1);}}>
            <option value="all">All statuses</option>
            <option value="draft">Draft</option>
            <option value="preparing">Preparing</option>
            <option value="prepared">Prepared</option>
            <option value="returned">Returned</option>
            <option value="reviewed">Reviewed</option>
            <option value="applied">Applied</option>
            <option value="publication_approved">Publication approved</option>
            <option value="candidate">Candidate</option>
            <option value="active">Active</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </label>

        <label>Reader records
          <select value={savedOutput} onChange={event=>{setSavedOutput(event.target.value);setSavedPage(1);}}>
            <option value="all">All types</option>
            <option value="case">Cases</option>
            <option value="news">News</option>
            <option value="article">Articles</option>
            <option value="update">Updates</option>
            <option value="none">No reader records</option>
          </select>
        </label>

        <label>Sort
          <select value={savedSort} onChange={event=>{setSavedSort(event.target.value);setSavedPage(1);}}>
            <option value="updated">Recently updated</option>
            <option value="oldest">Oldest updated</option>
            <option value="title">Title</option>
          </select>
        </label>

        <label>Rows
          <select value={savedPageSize} onChange={event=>{setSavedPageSize(Number(event.target.value));setSavedPage(1);}}>
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
          </select>
        </label>
      </div>

      {saved.error&&<p className="alert" role="alert">{saved.error}</p>}
      {saved.loading&&<p className="muted" role="status">Loading saved research…</p>}
      {!saved.loading&&saved.data&&!saved.data.items.length&&
        <p className="muted">No saved research matches these filters.</p>}

      {!!saved.data?.items.length&&
        <div className="saved-research-table-wrap">
          <table className="saved-research-table">
            <thead>
              <tr>
                <th>Research</th>
                <th>Status</th>
                <th>Updated</th>
                <th>Inputs</th>
                <th>Reader records</th>
                <th>Published</th>
                <th><span className="sr-only">Open</span></th>
              </tr>
            </thead>

            <tbody>
              {saved.data.items.map((item:Item)=>{
                const o=item.outputs??{};

                const records=[
                  ['Cases',Number(o.cases??0)],
                  ['News',Number(o.news??0)],
                  ['Articles',Number(o.articles??0)],
                  ['Updates',Number(o.updates??0)]
                ].filter(([,count])=>Number(count)>0);

                const published=[
                  ['Cases',Number(o.published_cases??0)],
                  ['News',Number(o.published_news??0)],
                  ['Articles',Number(o.published_articles??0)],
                  ['Updates',Number(o.published_updates??0)]
                ].filter(([,count])=>Number(count)>0);

                return <Fragment key={item.edition_id}>
                <tr key={item.edition_id} className={edition?.edition_id===item.edition_id?'saved-current':''}>
                  <td className="saved-title">
                    <strong>{item.title}</strong>
                    {item.focus&&item.focus!==item.title&&<small>{item.focus}</small>}
                  </td>

                  <td>
                    <span className={`saved-status status-${item.phase}`}>
                      {phaseLabel[item.phase]??String(item.phase).replaceAll('_',' ')}
                    </span>
                  </td>

                  <td className="saved-date">{stamp(item.updated_at)}</td>

                  <td className="saved-number">{item.selected_count}</td>

                  <td>
                    {records.length
                      ?records.map(([label,count])=>`${label} ${count}`).join(' · ')
                      :<span className="muted">None</span>}
                  </td>

                  <td>
                    {published.length
                      ?published.map(([label,count])=>`${label} ${count}`).join(' · ')
                      :<span className="muted">—</span>}
                  </td>

                  <td className="saved-open">
                    <button type="button" className="subtle" disabled={pending} onClick={()=>{if(expandedEditionId===item.edition_id){setExpandedEditionId(null);return;}setExpandedEditionId(item.edition_id);void resume(item.edition_id);}}>{expandedEditionId===item.edition_id?'Close':'Open'}</button>
                  </td>
                </tr>
                {expandedEditionId===item.edition_id&&<tr className="saved-evening-expanded">
                  <td colSpan={7}>
                    {restoring||edition?.edition_id!==item.edition_id
                      ? <p className="muted saved-evening-loading">Loading saved evening…</p>
                      : eveningWorkspace}
                  </td>
                </tr>}
              </Fragment>;
              })}
            </tbody>
          </table>
        </div>}

      <Pagination page={saved.data} onPage={setSavedPage} disabled={pending||saved.loading}/>
    </section>


    {(error||intake.error)&&<div className="alert" role="alert"><strong>{error?'The action could not finish.':'Intake could not load.'}</strong><p>{error||intake.error}</p><p>Your saved edition remains available. Refresh to recover its durable state.</p></div>}
    {notice&&<p className="notice" role="status">{notice}</p>}
    {expandedEditionId==='new'&&eveningWorkspace}
    <FeedSettings request={request}/>
    {expandedEditionId&&<footer className="desk-action-dock"><span>{selected.size} selected · {pending?'Preparation in progress':'Selection stays private'}</span>{selectionLocked?<button onClick={()=>{const review=document.getElementById("edition-review");review?.scrollIntoView({block:"start"});review?.focus({preventScroll:true});}}>Review saved return</button>:packetReady&&edition?<a className="button-link" href={`/api/editions/${edition.edition_id}/packets/${edition.packet.packet_id}/download`}>Download packet</a>:<button disabled={pending||!focus.trim()||packetReady} onClick={()=>void prepare()}>{packetReady?'Packet prepared':'Prepare one packet'}</button>}</footer>}
  </section>;
}
